import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Play, 
  Users, 
  Clock, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Settings as SettingsIcon,
  Heart,
  MessageCircle,
  Share,
  ChevronLeft,
  Grid,
  List
} from 'lucide-react';
import { useStream } from '../context/StreamContext';

function StreamViewer() {
  const { streamId } = useParams();
  const { streams, activeStreams } = useStream();
  
  const [selectedStream, setSelectedStream] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const [quality, setQuality] = useState('auto');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showChat, setShowChat] = useState(true);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    { id: 1, user: 'John', message: 'Great stream!', timestamp: new Date() },
    { id: 2, user: 'Alice', message: 'Love the content', timestamp: new Date() },
    { id: 3, user: 'Bob', message: 'Keep it up! 🔥', timestamp: new Date() },
  ]);
  
  const videoRef = useRef(null);

  useEffect(() => {
    if (streamId) {
      const stream = streams.find(s => s.id === streamId);
      if (stream) {
        setSelectedStream(stream);
        setIsPlaying(stream.status === 'live');
      }
    }
  }, [streamId, streams]);

  useEffect(() => {
    // Set up video player with HLS.js for better streaming support
    if (selectedStream?.playbackUrl && videoRef.current) {
      // In a real implementation, you'd use HLS.js here
      videoRef.current.src = selectedStream.playbackUrl;
    }
  }, [selectedStream]);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (isMuted) {
      handleVolumeChange(volume > 0 ? volume : 0.5);
    } else {
      handleVolumeChange(0);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (chatMessage.trim()) {
      const newMessage = {
        id: Date.now(),
        user: 'You',
        message: chatMessage,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, newMessage]);
      setChatMessage('');
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  if (streamId && !selectedStream) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Stream not found</p>
          <Link to="/viewer" className="btn-primary">
            Browse All Streams
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {selectedStream ? (
        <>
          {/* Stream Player Section */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            <div className="xl:col-span-3">
              {/* Video Player */}
              <div className="video-container aspect-video mb-4 group">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  poster={selectedStream.thumbnailUrl}
                  controls={false}
                  autoPlay={selectedStream.status === 'live'}
                />
                
                {/* Video Controls Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {/* Play/Pause Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={isPlaying ? handlePause : handlePlay}
                      className="w-20 h-20 bg-black bg-opacity-50 rounded-full flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                    >
                      <Play className={`w-8 h-8 text-white ${isPlaying ? 'hidden' : 'block'}`} />
                    </button>
                  </div>
                  
                  {/* Bottom Controls */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Volume Control */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={toggleMute}
                            className="text-white hover:text-cf-orange transition-colors duration-200"
                          >
                            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                          </button>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={isMuted ? 0 : volume}
                            onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                            className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        
                        {/* Quality Selector */}
                        <select
                          value={quality}
                          onChange={(e) => setQuality(e.target.value)}
                          className="bg-black bg-opacity-50 text-white border border-gray-600 rounded px-2 py-1 text-sm"
                        >
                          <option value="auto">Auto</option>
                          <option value="1080p">1080p</option>
                          <option value="720p">720p</option>
                          <option value="480p">480p</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button className="text-white hover:text-cf-orange transition-colors duration-200">
                          <SettingsIcon className="w-5 h-5" />
                        </button>
                        <button className="text-white hover:text-cf-orange transition-colors duration-200">
                          <Maximize className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Live Indicator */}
                {selectedStream.status === 'live' && (
                  <div className="absolute top-4 left-4">
                    <span className="status-live">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-2"></div>
                      LIVE
                    </span>
                  </div>
                )}
              </div>
              
              {/* Stream Info */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-white mb-2">{selectedStream.name}</h1>
                    <div className="flex items-center space-x-4 text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4" />
                        <span>{selectedStream.viewers || 0} viewers</span>
                      </div>
                      {selectedStream.duration > 0 && (
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDuration(selectedStream.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200">
                      <Heart className="w-5 h-5" />
                    </button>
                    <button className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200">
                      <Share className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Stream Description */}
                {selectedStream.meta?.description && (
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-gray-300">{selectedStream.meta.description}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Chat Sidebar */}
            <div className="xl:col-span-1">
              <div className="card h-96 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Live Chat</h3>
                  <button
                    onClick={() => setShowChat(!showChat)}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
                
                {showChat && (
                  <>
                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto space-y-2 mb-4">
                      {chatMessages.map(msg => (
                        <div key={msg.id} className="text-sm">
                          <span className="text-cf-orange font-medium">{msg.user}: </span>
                          <span className="text-gray-300">{msg.message}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Chat Input */}
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <input
                        type="text"
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-1 focus:ring-cf-orange focus:border-transparent"
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 bg-cf-orange hover:bg-orange-600 text-white rounded font-medium transition-colors duration-200"
                      >
                        Send
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Stream Browser */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Watch Streams</h1>
              <p className="text-gray-400">Browse and watch live streams from multiple hosts</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors duration-200 ${
                    viewMode === 'grid' ? 'bg-cf-orange text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors duration-200 ${
                    viewMode === 'list' ? 'bg-cf-orange text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Live Streams */}
          {activeStreams.length > 0 && (
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <h2 className="text-2xl font-bold text-white">Live Now</h2>
                <span className="bg-red-500 text-white px-2 py-1 rounded-full text-sm">
                  {activeStreams.length}
                </span>
              </div>
              
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
                {activeStreams.map((stream) => (
                  <StreamCard 
                    key={stream.id} 
                    stream={stream} 
                    viewMode={viewMode}
                    onClick={() => setSelectedStream(stream)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Streams */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">All Streams</h2>
            
            {streams.length === 0 ? (
              <div className="card text-center py-12">
                <Play className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-300 mb-2">No streams available</h3>
                <p className="text-gray-500">Check back later for live content</p>
              </div>
            ) : (
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
                {streams.map((stream) => (
                  <StreamCard 
                    key={stream.id} 
                    stream={stream} 
                    viewMode={viewMode}
                    onClick={() => setSelectedStream(stream)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Stream Card Component for the viewer
function StreamCard({ stream, viewMode, onClick }) {
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  if (viewMode === 'list') {
    return (
      <div 
        onClick={onClick}
        className="card flex items-center space-x-4 hover:shadow-xl transition-all duration-300 cursor-pointer group"
      >
        <div className="relative w-32 h-20 video-container">
          <img
            src={stream.thumbnailUrl || `https://via.placeholder.com/320x180/1f2937/9ca3af?text=${encodeURIComponent(stream.name)}`}
            alt={stream.name}
            className="w-full h-full object-cover"
          />
          {stream.status === 'live' && (
            <div className="absolute top-1 left-1">
              <span className="status-live text-xs">LIVE</span>
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white group-hover:text-cf-orange transition-colors duration-200">
            {stream.name}
          </h3>
          <div className="flex items-center space-x-4 text-gray-400 text-sm mt-1">
            <div className="flex items-center space-x-1">
              <Users className="w-4 h-4" />
              <span>{stream.viewers || 0}</span>
            </div>
            {stream.duration > 0 && (
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{formatDuration(stream.duration)}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <span className={stream.status === 'live' ? 'status-live' : 'status-offline'}>
            {stream.status?.toUpperCase() || 'OFFLINE'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={onClick}
      className="card group hover:shadow-xl transition-all duration-300 cursor-pointer"
    >
      <div className="relative video-container mb-4">
        <img
          src={stream.thumbnailUrl || `https://via.placeholder.com/640x360/1f2937/9ca3af?text=${encodeURIComponent(stream.name)}`}
          alt={stream.name}
          className="w-full h-48 object-cover"
        />
        
        <div className="absolute top-3 left-3">
          <span className={stream.status === 'live' ? 'status-live' : 'status-offline'}>
            <div className="w-2 h-2 bg-current rounded-full mr-2"></div>
            {stream.status === 'live' ? 'LIVE' : 'OFFLINE'}
          </span>
        </div>

        {stream.status === 'live' && stream.duration > 0 && (
          <div className="absolute bottom-3 right-3 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
            {formatDuration(stream.duration)}
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 group">
          <Play className="w-16 h-16 text-white opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-200" />
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-white truncate group-hover:text-cf-orange transition-colors duration-200">
            {stream.name}
          </h3>
          <p className="text-gray-400 text-sm">
            Created {new Date(stream.createdAt).toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Users className="w-4 h-4" />
              <span>{stream.viewers || 0}</span>
            </div>
            
            {stream.duration > 0 && (
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{formatDuration(stream.duration)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StreamViewer; 